import { useEffect, useState } from "react";
import { fetchPostureComparison } from "../utils/api";
import { publicationScansMatch } from "../components/regressionProvenance";

export default function usePostureComparison({ slug, report, scansPayload, currentScanId, previewPending, reportLoading, mode, comparisonScanId, retryKey = 0 }) {
  const publication = report?._publication;
  const scanId = publication?.selector;
  const identity = publication?.identity?.sha256;
  const identityPath = publication?.identity?.scored_path;
  const generation = publication?.state_generation;
  const matches = publicationScansMatch(report, scansPayload);
  const intentMatches = currentScanId != null ? scanId === currentScanId
    : scansPayload?.scans?.some(scan => scan.current === true && scan.scan_id === scanId
      && scan.identity?.scored_path === identityPath && scan.identity?.sha256 === identity);
  const requestMode = mode === "choose" && !comparisonScanId ? "baseline" : mode;
  const requestComparison = mode === "choose" ? comparisonScanId : null;
  const canRequest = !!slug && !!scanId && !!identity && !!identityPath && Number.isInteger(generation)
    && matches && intentMatches && !reportLoading && !previewPending && !report?._pending;
  const key = canRequest
    ? [slug, scanId, identityPath, identity, generation, requestMode, requestComparison || "", retryKey].join("|")
    : null;
  const [state, setState] = useState({ key: null, data: null, loading: false, error: false });
  const choiceKey = [slug || "", scanId || "", identityPath || "", identity || "", generation ?? ""].join("|");
  const [choiceState, setChoiceState] = useState({ key: null, choices: [] });

  useEffect(() => {
    if (!key) {
      setState({ key: null, data: null, loading: false, error: false });
      return undefined;
    }
    let cancelled = false;
    setState({ key, data: null, loading: true, error: false });
    fetchPostureComparison(slug, scanId, requestMode, requestComparison)
      .then(data => {
        if (!cancelled) {
          setState({ key, data, loading: false, error: false });
          if (data?.selected?.identity?.sha256 === identity
              && data?.selected?.identity?.scored_path === identityPath
              && data?.state_generation === generation) {
            setChoiceState({ key: choiceKey, choices: data.choices || [] });
          }
        }
      })
      .catch(() => {
        if (!cancelled) setState({ key, data: null, loading: false, error: true });
      });
    return () => { cancelled = true; };
  }, [key, slug, scanId, requestMode, requestComparison, identity, identityPath, generation, choiceKey]);

  const current = state.key === key;
  const data = current ? state.data : null;
  const responseMatches = data?.state_generation === generation
    && data?.selected?.selector === scanId
    && data?.selected?.identity?.sha256 === identity
    && data?.selected?.identity?.scored_path === identityPath
    && (mode !== "choose" || !comparisonScanId || !data?.available || data?.comparison?.selector === comparisonScanId);
  return {
    data: mode === "choose" && !comparisonScanId ? null : responseMatches ? data : null,
    selected: responseMatches ? data?.selected : null,
    loading: !!key && (!current || state.loading),
    error: current && state.error,
    waiting: !key,
    choices: choiceState.key === choiceKey ? choiceState.choices : [],
  };
}
